import {useProject, useProjectId} from "../shopdrawing/ProjectName";
import {useSessionStorage} from "react-use";
import {useCallback, useEffect, useState} from "react";
import {Project, QuoteContractor} from "../../../api/Projects";
import {useSyncedRef} from "../../../misc/pdf/parts/PageContext";
import {
    Button,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem
} from "@mui/material";
import {Dialog2} from "../../../misc/Dialog2";
import {SmoothActionBar} from "../../../misc/SmoothActionBar";
import {SelectWithLabel} from "./hardware/SelectWithLabel";
import {Contact} from "../../../api/Contacts";
import {api} from "../../../api/API";
import EngineeringIcon from '@mui/icons-material/Engineering';

export function useCurrentContractor(props?: {
    onAutoPromptCanceled?(): void;
}) {
    const projectId = useProjectId();
    const proj = useProject();

    const [quoteContractorId, setQuoteContractorId] = useSessionStorage<number|null>(`current-quote-contractor-${projectId}`, null);
    useEffect(() => {
        if(!proj) return;
        if(proj.quoteContractors.some(v => v.id === quoteContractorId)) {
            return;
        }

        if(proj.quoteContractors.length > 0) {
            let contractor = proj.quoteContractors.find(v => v.contractor === proj.contractor)
            if(!contractor) {
                contractor = proj.quoteContractors[0];
            }

            setQuoteContractorId(contractor.id);
            return;
        }
    }, [proj, quoteContractorId, setQuoteContractorId]);


    const getContractorObj = (quoteContractorId: number | null) => {
        if(proj?.stage === "quote") {
            if (quoteContractorId) {
                // @ts-ignore
                return proj?.quoteContractors.find(v => v.id === quoteContractorId);
            }
        } else {
            if(proj) {
                if(proj.contractor) {
                    return {
                        id: 0,
                        contractor: proj.contractor || 0,
                        contractorName: proj.contractorName || "",
                        contact: proj.gcContact,
                        contactName: proj.gcContactName || "",
                        contactEmail: proj.gcContactEmail || "",
                        contactPhone: proj.gcContactPhone || "",
                        archived: false,
                        note: "",
                    }
                } else if(proj.pmContact && proj.client) {
                    return {
                        id: 0,
                        contractor: proj.client || 0,
                        contractorName: proj.clientName || "",
                        contact: proj.pmContact,
                        contactName: proj.pmContactName || "",
                        contactEmail: proj.pmContactEmail || "",
                        contactPhone: proj.pmContactPhone || "",
                        archived: false,
                        note: "",
                    }
                }
            }
        }

        return undefined
    }

    const getContractorObjRef = useSyncedRef(getContractorObj)
    const contractor: QuoteContractor | undefined = getContractorObj(quoteContractorId);
    const contractorRef = useSyncedRef(contractor);
    const [promptForContractor, setPromptForContractor] = useState<{resolve: (c: QuoteContractor|null) => void, reject: (e: Error) => void}|null>(null);

    const getContractor = useCallback(() => {
        return new Promise<QuoteContractor|null>((resolve, reject) => {
            if(contractorRef.current) {
                resolve(contractorRef.current);
                return;
            }

            setPromptForContractor({
                resolve: resolve,
                reject: reject,
            });
        });
    }, [contractorRef]);

    const onAutoPromptCancelledRef = useSyncedRef(props?.onAutoPromptCanceled);
    useEffect(() => {
        if(!onAutoPromptCancelledRef.current) return;
        getContractor().then(() => {}, onAutoPromptCancelledRef.current);
    }, [getContractor, onAutoPromptCancelledRef]);

    const showSelector = useCallback(() => {
        setPromptForContractor({
            resolve: () => {},
            reject: () => {},
        });
    }, []);

    const getContractorDetails = useCallback(async () => {
        const contractor = contractorRef.current;
        if(!contractor) {
            return {
                client: undefined,
                clientContact: undefined,
            }
        }

        const clientP = api.companies.get({id: contractor.contractor});
        let contactP: Promise<Contact|undefined> = Promise.resolve(undefined);
        if(contractor.contact) {
            contactP = api.contacts.get({id: contractor.contact});
        }

        const client = await clientP;
        const contact = await contactP;

        return {
            client: client,
            clientContact: contact,
        }
    }, [contractorRef]);

    let Element: any = null;
    if(promptForContractor) {
        const reject = promptForContractor.reject;
        const resolve = promptForContractor.resolve;

        Element = (
            <PromptForContractor
                project={proj}
                value={quoteContractorId}
                onSelect={v => {
                    console.log("Selected contractor", v);
                    setQuoteContractorId(v ? v.id : null);
                    contractorRef.current = getContractorObjRef.current(v ? v.id : null);
                    setPromptForContractor(null);
                    resolve(v);
                }} onCancel={() => {
                setPromptForContractor(null);
                reject(new Error("User cancelled contractor selection"));
            }} />
        );
    }

    console.log("quoteContractorId", quoteContractorId, contractor);

    return {
        contractor,
        getContractorDetails,
        setContractor: setQuoteContractorId,
        autoPromptContractor: getContractor,
        showSelector: showSelector,
        CurrentContractorPromptElement: Element,
    }
}

function PromptForContractor(props: {
    value: number | null;
    project?: Project | null;
    onSelect(c: QuoteContractor|null): void;
    onCancel(): void;
}) {
    const [value, setValue] = useState<number|null>(props.value);

    const submit = () => {
        if(!props.project) return;

        const contractor = props.project.quoteContractors.find(v => v.id === value);
        if(contractor) {
            props.onSelect(contractor);
        } else {
            props.onSelect(null as any);
        }
    };

    return (
        <Dialog2 open onClose={props.onCancel} onEnterPress={submit}>
            <DialogTitle>Choose a Contractor</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <div>Your selection will be saved for this session.</div>
                    <div>This value is used for the cover pages of PDF submittals</div>
                    <div>You can edit this list in the project overview window</div>
                </DialogContentText>
                <div style={{height: 16}}></div>

                <SelectWithLabel fullWidth label="Contractor" value={value} onChange={e => {
                    setValue(parseInt(e.target.value as string));
                }}>
                    {props.project?.quoteContractors.map(c =>
                            <MenuItem value={c.id.toString()}>
                                <div>
                                    <div>{c.contractorName}</div>
                                    <div style={{fontSize: "0.8em"}}>{c.contactName || "(no contact)"}</div>
                                </div>
                            </MenuItem>)}
                </SelectWithLabel>

            </DialogContent>
            <SmoothActionBar
                onCancel={props.onCancel}
                onSubmit={submit}
            />
        </Dialog2>
    )
}

export function CurrentContractorDisplay() {
    const cnt = useCurrentContractor();

    return (
        <>
            <Button startIcon={<EngineeringIcon />} color="inherit" size="small" onClick={() => {
                cnt.showSelector();
            }}>
                <div style={{
                    fontSize: "0.75rem",
                    textAlign: "center",
                }}>
                    {short(cnt.contractor?.contractorName || "", 8) || "none"}
                </div>
            </Button>
            {cnt.CurrentContractorPromptElement}
        </>
    )
}

export function short(name: string, maxLength: number) {
    if(!name) return "";

    const parts = name.split(" ");
    let value = parts[0];
    if(value.length > maxLength) {
        return value.slice(0, maxLength)
    }

    return value;
}